const buildQuantityOptions = (availableQty) => {
  if (Array.isArray(availableQty)) {
    return availableQty;
  }
  const qty = Number(availableQty || 0);
  if (!Number.isFinite(qty) || qty <= 0) {
    return [];
  }
  return Array.from({ length: qty }, (_, idx) => idx + 1);
};

const normalizeImagesToLocations = (images) => {
  if (!images || !Array.isArray(images)) return [];
  return images
    .map((img) => {
      if (!img) return null;
      if (typeof img === 'string') {
        return { Location: img };
      }
      if (typeof img === 'object') {
        return {
          Location: img.Location || img.url || img.src || ''
        };
      }
      return null;
    })
    .filter((img) => img && img.Location);
};

const mapRegionShippingCosts = (regionCosts = {}, prefix) => ({
  [`${prefix}FreeShipping`]: regionCosts.freeShipping || 'No',
  [`${prefix}StandardRate`]: regionCosts.standardRate ?? 0,
  [`${prefix}StandardPerWeight`]: regionCosts.standardPerWeight ?? 0,
  [`${prefix}StandardPerItem`]: regionCosts.standardPerItem ?? 0,
  [`${prefix}ExpeditedShippingRate`]: regionCosts.expeditedRate ?? 0,
  [`${prefix}ExpeditedShippingPerWeight`]: regionCosts.expeditedPerWeight ?? 0,
  [`${prefix}ExpeditedShippingPerItem`]: regionCosts.expeditedPerItem ?? 0,
  [`${prefix}TwoDayDeliveryRate`]: regionCosts.twoDayRate ?? 0,
  [`${prefix}TwoDayDeliveryPerWeight`]: regionCosts.twoDayPerWeight ?? 0,
  [`${prefix}TwoDayDeliveryPerItem`]: regionCosts.twoDayPerItem ?? 0,
  [`${prefix}ThreeFiveDayDeliveryRate`]: regionCosts.threeFiveRate ?? 0,
  [`${prefix}ThreeFiveDayDeliveryPerWeight`]: regionCosts.threeFivePerWeight ?? 0,
  [`${prefix}ThreeFiveDayDeliveryPerItem`]: regionCosts.threeFivePerItem ?? 0
});

const normalizeLegacyShipping = (product) => {
  if (!product || !product.shippingCosts) return product;
  const alreadyHasLegacy =
    product.usShippingCosts ||
    product.caShippingCosts ||
    product.euShippingCosts ||
    product.asShippingCosts ||
    product.meShippingCosts ||
    product.afShippingCosts;
  if (alreadyHasLegacy) return product;

  return {
    ...product,
    usShippingCosts: mapRegionShippingCosts(product.shippingCosts.us, 'us'),
    caShippingCosts: mapRegionShippingCosts(product.shippingCosts.ca, 'ca'),
    euShippingCosts: mapRegionShippingCosts(product.shippingCosts.eu, 'eu'),
    asShippingCosts: mapRegionShippingCosts(product.shippingCosts.as, 'as'),
    meShippingCosts: mapRegionShippingCosts(product.shippingCosts.me, 'me'),
    afShippingCosts: mapRegionShippingCosts(product.shippingCosts.af, 'af')
  };
};

const normalizeCartProductEntry = (entry, fallbackQuantityOptions) => {
  if (!entry || typeof entry !== 'object') return entry;
  const normalizedEntry = { ...entry };

  const quantityOptions = buildQuantityOptions(
    normalizedEntry.quantity || fallbackQuantityOptions
  );
  if (quantityOptions.length > 0) {
    normalizedEntry.quantity = quantityOptions;
    if (!normalizedEntry.count) {
      normalizedEntry.count = quantityOptions[0];
    }
  }

  if (normalizedEntry.image && !normalizedEntry.imagesArray) {
    normalizedEntry.imagesArray = normalizeImagesToLocations([normalizedEntry.image]);
  } else if (normalizedEntry.imagesArray) {
    normalizedEntry.imagesArray = normalizeImagesToLocations(normalizedEntry.imagesArray);
  }

  return normalizedEntry;
};

const normalizeCartItem = (item) => {
  if (!item || typeof item !== 'object') return item;

  const normalizedImages = normalizeImagesToLocations(item.images || []);
  const baseQuantity =
    item.baseQuantity ??
    item.availableQuantity ??
    (Array.isArray(item.quantity) ? item.quantity.length : item.quantity);

  let normalizedItem = {
    ...item,
    images: normalizedImages.length > 0 ? normalizedImages : item.images
  };

  if (Array.isArray(item.cartProduct)) {
    normalizedItem.cartProduct = item.cartProduct.map((entry) =>
      normalizeCartProductEntry(entry, baseQuantity)
    );
  } else {
    const count = item.count ?? item.quantity ?? 1;
    const quantityOptions = buildQuantityOptions(baseQuantity);
    normalizedItem.cartProduct = [
      normalizeCartProductEntry(
        {
          imagesArray: normalizedImages,
          brand: item.brand || item.brandName || item.brands?.[0]?.name || '',
          price: item.price ?? item.basePrice ?? 0,
          discount: item.discount ?? item.baseDiscount ?? 0,
          color: item.color || '',
          sizeName: item.sizeName || '',
          count,
          quantity: quantityOptions
        },
        baseQuantity
      )
    ];
  }

  normalizedItem = normalizeLegacyShipping(normalizedItem);

  return normalizedItem;
};

export const normalizeCartItems = (cart) => {
  if (!Array.isArray(cart)) return [];
  return cart.map(normalizeCartItem).filter(Boolean);
};
